import {Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {FC, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {SaleTagIcon} from '../../atoms/SaleTagIcon/SaleTagIcon';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {Text} from '../../atoms/Text/Text';
import {CountdownBoxed} from '../Countdown/CountdownBoxed';

export interface DiscountToastCountdown {
  endsAt: Date;
}

export interface DiscountToastColors {
  backgroundColor: string;
  foregroundColor: string;
}

export interface DiscountToastProps {
  headline: string;
  description: string;
  title?: string;
  countdown?: DiscountToastCountdown;
  colors: DiscountToastColors;
  onClick: () => void;
  onClose?: () => void;
  closeLabel: string;
  className?: string;
  ['data-testid']?: string;
}

// Anchor id rendered by both app headers inside their shop-hint containers, so
// the portalled toast docks below the sticky header instead of overlaying it.
export const DISCOUNT_TOAST_PORTAL_ID = 'discount-toast-portal';

/**
 * Presentational discount-nudge card matching the ADP CampaignOverlay. Manages
 * its own slide-in/dismiss state; placement is the consumer's responsibility.
 */
export const DiscountToast: FC<DiscountToastProps> = ({
  headline,
  description,
  title,
  countdown,
  colors,
  onClick,
  onClose,
  closeLabel,
  className,
  'data-testid': dataTestid,
}) => {
  const [open, setOpen] = useState(true);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <ToastRoot className={className} data-testid={dataTestid}>
      <Slide mounted={open} direction="down" duration={400}>
        <CardWrapper>
          <ToastHeader style={{backgroundColor: colors.backgroundColor}}>
            {countdown ? (
              <CountdownBoxed
                endDate={countdown.endsAt}
                size="small"
                backgroundColor={colors.backgroundColor}
                fontColor={colors.foregroundColor}
                light
              />
            ) : (
              title && (
                <Text size="px10" style={{color: colors.foregroundColor}}>
                  {title}
                </Text>
              )
            )}
            <CloseButton onClick={handleClose} aria-label={closeLabel}>
              <Icon
                asset={GenericIconName.Close}
                size="XS"
                fill={colors.foregroundColor}
                aria-hidden
              />
            </CloseButton>
          </ToastHeader>

          <ToastBody
            onClick={onClick}
            aria-label={`${headline} — ${description}`}
          >
            <SaleTagIcon
              backgroundColor={colors.backgroundColor}
              foregroundColor={colors.foregroundColor}
              size="XL"
            />
            <TextGroup>
              <ToastHeadline>{headline}</ToastHeadline>
              <ToastDescription>{description}</ToastDescription>
            </TextGroup>
          </ToastBody>
        </CardWrapper>
      </Slide>
    </ToastRoot>
  );
};

// Position-neutral: the consumer owns placement; the card fills the given width.
const ToastRoot = styled.div`
  width: 100%;
`;

const CardWrapper = styled.div`
  border-radius: 0.5rem;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  margin: 0.75rem 1rem 1rem;
`;

// Plain div (not a button) so the countdown/title text stays readable by
// assistive tech and isn't swallowed by a button's aria-label.
const ToastHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;
`;

const CloseButton = styled(UnstyledButton)`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  &:focus-visible {
    ${tabFocusedStyles({offset: '-2px'})}
  }
`;

const ToastBody = styled(UnstyledButton)`
  display: flex;
  width: 100%;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem 1rem;
  align-items: center;
  background: ${COLORS.White};
  cursor: pointer;
  text-align: start;

  &:focus-visible {
    ${tabFocusedStyles({offset: '-2px'})}
  }
`;

const TextGroup = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ToastHeadline = styled.span`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 20px;
  color: ${COLORS.Black};
`;

const ToastDescription = styled.span`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.w400};
  line-height: 18px;
  color: ${COLORS.Black};
`;
